// Buttons. Real <button type="button">, >= 44px targets, `disabled` keeps the button focusable (aria-disabled) and
// swallows clicks, as the prototype's disabled "Next".
//   <PrimaryButton onClick={next}>Next</PrimaryButton>              accent bg, --on text, 800 17px, 58 tall
//   <PrimaryButton icon="play" h={54}>Start</PrimaryButton>          card Start
//   <PrimaryButton variant="cream">Continue</PrimaryButton>          O1 (before a buddy)
//   <SecondaryButton icon="lifebuoy">Stuck</SecondaryButton>         solid secondary
//   <TextButton onClick={...}>Change something</TextButton>          quiet text action
//   <IconButton icon="trash" label="Remove card" variant="outline" size={54} />
import type { ComponentChildren, JSX } from 'preact';
import { Icon, type IconName } from './icons';
import s from './buttons.module.css';

interface Base {
  children?: ComponentChildren;
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  /** Busy: shows the label dimmed and ignores clicks (aria-busy). */
  busy?: boolean;
  class?: string;
  style?: JSX.CSSProperties;
  'aria-label'?: string;
  'aria-pressed'?: boolean | 'true' | 'false';
  'aria-expanded'?: boolean | 'true' | 'false';
  id?: string;
  'data-cta'?: string;
}

function guard(p: Base) {
  return (e: MouseEvent) => { if (p.disabled || p.busy) { e.preventDefault(); return; } p.onClick?.(e); };
}

const radiusFor = (h: number) => (h >= 56 ? 18 : h >= 50 ? 16 : 14);
const fontFor = (h: number) => (h >= 50 ? 17 : 15);

export interface PrimaryProps extends Base {
  /** accent (default) | cream (O1, before a buddy is picked) */
  variant?: 'accent' | 'cream';
  /** Height in px (default 58). Radius and font follow the prototype: >=56 -> 18/17px, >=50 -> 16/17px, else 14/15px. */
  h?: number;
  icon?: IconName;
  /** Icon drawn filled (play) instead of stroked (check). Default: play is filled. */
  iconFill?: boolean;
  /** Grow factor inside a flex row (Start is 1.35 next to Stuck's 1). */
  grow?: number;
}

export function PrimaryButton(p: PrimaryProps) {
  const h = p.h ?? 58;
  const fillIcon = p.iconFill ?? p.icon === 'play';
  return (
    <button
      type="button" id={p.id} class={[s.btn, s.primary, p.variant === 'cream' && s.cream, p.class].filter(Boolean).join(' ')}
      style={{ height: h, borderRadius: radiusFor(h), fontSize: fontFor(h), flex: p.grow != null ? `${p.grow} 1 0` : undefined, ...p.style }}
      aria-disabled={p.disabled ? 'true' : undefined} aria-busy={p.busy ? 'true' : undefined} aria-label={p['aria-label']}
      data-cta={p['data-cta']} onClick={guard(p)}
    >
      {p.icon && <Icon name={p.icon} size={18} color={fillIcon ? 'none' : 'currentColor'} fill={fillIcon ? 'currentColor' : 'none'} strokeWidth={p.icon === 'check' ? 2.8 : 2} />}
      <span>{p.children}</span>
    </button>
  );
}

export interface SecondaryProps extends Base { h?: number; icon?: IconName; grow?: number }
export function SecondaryButton(p: SecondaryProps) {
  const h = p.h ?? 56;
  return (
    <button
      type="button" id={p.id} class={[s.btn, s.secondary, p.class].filter(Boolean).join(' ')}
      style={{ height: h, borderRadius: radiusFor(h), fontSize: h >= 50 ? 16 : 15, flex: p.grow != null ? `${p.grow} 1 0` : undefined, ...p.style }}
      aria-disabled={p.disabled ? 'true' : undefined} aria-busy={p.busy ? 'true' : undefined} aria-label={p['aria-label']}
      aria-pressed={p['aria-pressed']} onClick={guard(p)}
    >
      {p.icon && <Icon name={p.icon} size={18} />}
      <span>{p.children}</span>
    </button>
  );
}

export interface TextButtonProps extends Base {
  /** muted (#CFC8BC, default) | accent | danger */
  tone?: 'muted' | 'accent' | 'danger';
  underline?: boolean;
  icon?: IconName;
  /** Font weight (default 700) and size (default 15). */
  weight?: number;
  size?: number;
}
export function TextButton(p: TextButtonProps) {
  return (
    <button
      type="button" id={p.id} class={[s.text, p.tone === 'accent' && s.toneAcc, p.tone === 'danger' && s.toneDanger, p.underline && s.underline, p.class].filter(Boolean).join(' ')}
      style={{ fontWeight: p.weight ?? 700, fontSize: p.size ?? 15, ...p.style }}
      aria-disabled={p.disabled ? 'true' : undefined} aria-label={p['aria-label']} aria-expanded={p['aria-expanded']} onClick={guard(p)}
    >
      {p.icon && <Icon name={p.icon} size={18} />}
      <span>{p.children}</span>
    </button>
  );
}

export interface IconButtonProps extends Base {
  icon: IconName;
  /** Required: what the button does ("Remove card", "Back", "Stop working"). */
  label: string;
  /** glass (dark translucent with a hairline, default) | ghost (transparent) | outline (card Remove) | solid (accent) */
  variant?: 'glass' | 'ghost' | 'outline' | 'solid';
  size?: number;
  iconSize?: number;
  /** Rounded square (radius 16) instead of a circle. */
  square?: boolean;
  iconColor?: string;
}
export function IconButton(p: IconButtonProps) {
  const size = p.size ?? 44;
  return (
    <button
      type="button" id={p.id}
      class={[s.icon, s['v_' + (p.variant || 'glass')], p.class].filter(Boolean).join(' ')}
      style={{ width: size, height: size, borderRadius: p.square ? radiusFor(size) : size / 2, ...p.style }}
      aria-label={p.label} aria-disabled={p.disabled ? 'true' : undefined} aria-pressed={p['aria-pressed']} onClick={guard(p)}
    >
      <Icon name={p.icon} size={p.iconSize ?? Math.round(size * 0.42)} color={p.iconColor} fill={p.icon === 'play' ? (p.iconColor || 'currentColor') : 'none'} />
    </button>
  );
}

/** The fixed card button row (SPEC §0/§3): Start (accent, widest) · Stuck (solid secondary) · Remove (trash icon). */
export function CardButtons({ onStart, onStuck, onRemove, title, h = 54, startLabel = 'Start' }: {
  onStart: () => void; onStuck: () => void; onRemove?: () => void; title: string; h?: number; startLabel?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <PrimaryButton h={h} icon="play" grow={1.35} onClick={onStart}>{startLabel}</PrimaryButton>
      <SecondaryButton h={h} icon="lifebuoy" grow={1} onClick={onStuck}>Stuck</SecondaryButton>
      {onRemove && <IconButton icon="trash" label={`Remove card: ${title}`} variant="outline" size={h} square iconSize={20} iconColor="#B9B2A6" onClick={onRemove} />}
    </div>
  );
}
